import * as Sentry from '@sentry/react';
import {
	browserTracingIntegration,
	captureConsoleIntegration,
	init as initSentry,
	replayIntegration,
	setTag
} from '@sentry/react';

import { isCI, isDev } from 'src/common/helpers/EnvironmentHelper';
import { ITenant } from 'src/common/interfaces/Tenant';
import { IUser } from 'src/common/interfaces/User';
import { Logger } from 'src/common/services/Logger';
import { beforeSendDropNoise } from 'src/common/services/SentryBeforeSend';

const PRODUCT_NAME = 'platform';
const SENTRY_PLACEHOLDER = 'xxxxx';
const SENTRY_REPLAY_SESSION_SAMPLE_RATE = 0;
const SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE = 1.0;
const SENTRY_TRACES_SAMPLE_RATE = 0.2;

let initialized = false;

const isValidDsn = (dsn: string | undefined): boolean =>
	!!dsn && dsn !== SENTRY_PLACEHOLDER && !dsn.startsWith('x');

export const isSentryEnabled = (): boolean => {
	return !isDev() && !isCI();
};

export const InitSentry = (): void => {
	try {
		if (initialized || !isSentryEnabled() || !isValidDsn(import.meta.env.VITE_SENTRY_DSN)) {
			return;
		}

		initSentry({
			dsn: import.meta.env.VITE_SENTRY_DSN,
			integrations: [
				browserTracingIntegration(),
				replayIntegration({
					maskAllText: false,
					blockAllMedia: false
				}),
				captureConsoleIntegration({ levels: ['error'] })
			],
			tracesSampleRate: SENTRY_TRACES_SAMPLE_RATE,
			replaysSessionSampleRate: SENTRY_REPLAY_SESSION_SAMPLE_RATE,
			replaysOnErrorSampleRate: SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE,
			// eslint-disable-next-line no-undef
			release: `${UI_VERSION}`,
			environment: import.meta.env.VITE_RUN_ENV,
			ignoreErrors: ['Non-Error promise rejection captured'],
			beforeSend: beforeSendDropNoise
		});

		setTag('project-id', import.meta.env.VITE_SENTRY_PROJECT_ID);
		setTag('product-name', PRODUCT_NAME);
		initialized = true;
	} catch (error) {
		Logger.error('Error initializing Sentry', error);
	}
};

export const initSentryUser = (user: IUser, tenant: ITenant): void => {
	try {
		const userInfo = {
			email: user.email ?? '',
			role: user.role.name ?? '',
			roleType: user.role.type ?? ''
		};

		const tenantInfo = {
			accountPlan: tenant.accountPlan ?? '',
			companyName: tenant.companyName ?? '',
			id: tenant.id ?? '',
			name: tenant.name ?? ''
		};

		Sentry.setUser({
			id: user.id,
			username: `${user.name ?? ''} ${user.surname ?? ''}`,
			...tenantInfo,
			...userInfo
		});

		if (tenant.id) {
			setTag('tenant_id', tenant.id);
		}

		const tenantName = tenant.companyName ?? tenant.name;

		if (tenantName) {
			setTag('tenant_name', tenantName);
		}
	} catch (error) {
		Logger.error('Error initializing Sentry user', error);
	}
};
